/**
 * Boot Session Identifier
 *
 * Generates a unique boot_id on app startup for correlating all requests
 * and errors that occur during a single browser session/page load.
 *
 * This helps debug "first load failures" by grouping all events from
 * a single app initialization sequence.
 */

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Generate boot_id once when this module is first imported
const BOOT_ID = generateUUID();
const BOOT_TIMESTAMP = Date.now();

// Boot attempt counter - updated by DashboardBootstrap on retries
let bootAttempt = 1;

/**
 * Get the current boot session ID.
 * This ID remains constant for the lifetime of the page/app session.
 */
export function getBootId(): string {
  return BOOT_ID;
}

/**
 * Get the boot timestamp (when the app was initialized).
 */
export function getBootTimestamp(): number {
  return BOOT_TIMESTAMP;
}

/**
 * Get the time since boot in milliseconds.
 */
export function getTimeSinceBoot(): number {
  return Date.now() - BOOT_TIMESTAMP;
}

/**
 * Check if we're in the "early boot" phase (first 30 seconds).
 * Useful for identifying first-load failures.
 */
export function isEarlyBoot(): boolean {
  return getTimeSinceBoot() < 30000;
}

/**
 * Get the current boot attempt number.
 * Starts at 1, incremented on retry.
 */
export function getBootAttempt(): number {
  return bootAttempt;
}

/**
 * Set the boot attempt number (called by DashboardBootstrap on retry).
 */
export function setBootAttempt(attempt: number): void {
  bootAttempt = attempt;
}

/**
 * Boot session context for Sentry and logging.
 */
export interface BootSessionContext {
  boot_id: string;
  boot_timestamp: number;
  time_since_boot_ms: number;
  is_early_boot: boolean;
  boot_attempt: number;
}

/**
 * Get the full boot session context.
 */
export function getBootSessionContext(): BootSessionContext {
  return {
    boot_id: BOOT_ID,
    boot_timestamp: BOOT_TIMESTAMP,
    time_since_boot_ms: getTimeSinceBoot(),
    is_early_boot: isEarlyBoot(),
    boot_attempt: bootAttempt,
  };
}
